import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { closeSocket, openSocket } from '~/helpers/socketHelper';
import { ACC_TOKEN_JWT_EXPIRED, ACC_TOKEN_MISSING } from '~/helpers/errorHelper';
import { refreshAccessToken } from '~/helpers/apiHelper';
import { Socket } from 'socket.io-client';

type SocketContextValue = {
  socket: Socket | null;
  socketEmit: (event: string, data?: any) => void;
};

export const SocketContext = React.createContext<SocketContextValue>({ socket: null, socketEmit: () => {} });

type SocketProviderProps = {
  children: React.ReactNode;
};

const SocketProvider = (props: SocketProviderProps) => {
  // we use a ref to store the socket client as it won't be updated frequently
  // when setting the initial value of the ref, mind the following issue
  // https://beta.reactjs.org/reference/react/useRef#avoiding-recreating-the-ref-contents
  const socket = useRef<Socket | null>(null);
  if (socket.current === null) {
    socket.current = openSocket();
  }

  // store the socket id in state
  // this will be updated every time the socket connects
  // we do not actually need the value
  // but we need a way to re-render the descendant components when the socket disconnects and reconnects
  // allowing them to re-subscribe to events with the new socket connection
  const [socketId, setSocketId] = useState<string | undefined>(socket.current?.id);
  // store the connection error in state
  // we do not actually need the value
  // but we need a way to wait untill the socket either connects or fails to connect
  const [connErr, setConnErr] = useState<boolean | undefined>(undefined);

  // handler that helps emit an event
  const socketEmit = useCallback((event: string, data?: any) => {
    if (socket.current) {
      socket.current.emit(event, data);
    }
  }, []); // socket is a ref — always the same object

  useEffect(() => {
    if (socket.current) {
      socket.current.on('connect', () => {
        console.log('Socket connected. Id', socket.current?.id);
        setSocketId(socket.current?.id);
      });
      socket.current.on('connect_error', (err: any) => {
        console.error('Socket connection error:', err.data || err);
        // if this is the first connection attempt then, because it failed, there is no socketId
        // so we need another way to let the execution continue
        if (!socketId) {
          setConnErr(true);
        }
        // check if the client is unable to connect because the access token has expired
        // maybe because the app has been idle for a while
        if (shouldRefreshToken(err)) {
          // refresh the access token
          refreshAccessToken(err.config.url)
            .then(() => {
              console.log('Socket access token refreshed');
              // in this case the socket will not reconnect automatically
              if (socket.current) {
                socket.current.connect();
              }
            })
            .catch(ex => console.error('Socket refresh access token failed', ex));
        }
      });
      socket.current.on('error', (err: any) => {
        console.error('Socket error:', err);
      });
      socket.current.on('disconnect', (reason: string) => {
        console.error('Socket disconnected:', reason);
      });
      socket.current.io.on('reconnect_attempt', () => {
        console.log('Socket attempting to reconnect');
      });
      socket.current.io.on('reconnect', () => {
        console.log('Socket reconnected');
      });
    }
    // Remove all the listeners and close the socket upon unmount
    return () => closeSocket(socket.current);
  }, []);

  // The socket connects in the background — children render immediately.
  // Socket.IO handles pre-connection operations gracefully (queues emits, registers listeners).
  // When the socket connects, socketId changes, triggering a context update so consumers re-subscribe.
  const value = useMemo(
    () => ({ socket: socket.current, socketEmit }),
    [socketId, socketEmit] // socketId triggers recompute on reconnect so consumers re-subscribe
  );

  return <SocketContext.Provider value={value} {...props} />;
};

const shouldRefreshToken = (error: any): boolean => !!(error.data && [ACC_TOKEN_MISSING, ACC_TOKEN_JWT_EXPIRED].includes(error.data.code));

export const useSocket = (): SocketContextValue => React.useContext(SocketContext);

export default SocketProvider;

